/**
 * Statische Utility-Klasse zu Zahlen und Mathematik
 */
export default class Maths {

    /** */
    static round(number, decimals=2) {
        const f = Math.pow(10,decimals);
        return Math.round( number * f ) / f ;
    }

    /** */
    static roundFixed(number, decimals=2) {
        return Maths.round(number, decimals).toFixed(decimals);
    }

    /** */
    static floor(number, decimals=2) {
        const f = Math.pow(10,decimals);
        return Math.floor( number * f ) / f ;
    }

    /** */
    static ceil(number, decimals=2) {
        const f = Math.pow(10,decimals);
        return Math.ceil( number * f ) / f ;
    }

    /**
     * Rundet eine Zahl auf die nächsthöhere ganzzahlige Potenz auf
     */
    static ceilToNextPowerOf(x, power = 2) {
        return Math.pow(power, Math.ceil(Math.log(x) / Math.log(power)));
    }

    /**
     * Rundet eine Zahl auf die letzte ganzzahlige Potenz ab
     */
    static floorToLastPowerOf(x, power = 2) {
        return this.ceilToNextPowerOf(x, 1 / power);
    }

    /**
     * Begrenzt eine Zahl auf das Interval [min,max]
     */
    static cutBetween(min, number, max) {
        return Math.min(max, Math.max(number, min));
    }

    /**
     * Bildet number linear auf das Einheitsintervall [0,1] ab und begrenzt, falls der Wert ausserhalb liegen würde.
     * min->0, max->1.
     */
    static mapToUnitInterval(min, number, max) {
        return number < min ? 0 :
            number > max ? 1 :
                min >= max ? 0 :
                    (number - min) / (max - min);
    }

    /**
     * Integriert eine Funktion f im Intervall [start,end] in Anzahl steps diskreten Schritten
     * nach der Trapezmethode.
     */
    static trapezoidalIntegration(f, start, end, steps) {
        var delta = (end - start) / steps;
        var sum = f(start) + f(end);
        for (var i = 1; i < steps; i++) {
            sum += 2 * f(start + i * delta);
        }
        return sum * delta / 2;
    }

    /**
     * Testet, ob eine Zahl keine endliche, echte Zahl ist: null, undefined, NaN, infinite ergeben true.
     */
    static notFinite(number) {
        return number === null || typeof number === 'undefined' || Number.isNaN(number) || !Number.isFinite(number);
    }

    /** */
    static isFinite(number) {
        return !this.notFinite(number);
    }

    /**
     * bewegt a um b in Richtung 0
     */
    static sumToZero(a, b) {
        if (a < 0) {
            b = -b;
        }
        return a + b;
    }

    /**
     * Cantor-Pairing. Erzeugt aus n Zahlen eine neue eindeutige Zahl. Somit können
     * z.B. per Zufallszahl erzeugte IDs eindeutig zu einer neuen ID kombiniert werden.
     * Achtung! Die Zahlen sind nur eindeutig, wenn die Dimension des Pairings gleich ist,
     * wenn also in einem Context stets die gleiche Argument-Anzahl verwendet wird!
     * Weiterhin ist zu beachten, dass die benötigte Anzahl der Bits sich ver-n-facht,
     * und Javascript-Zahlen maximal 53 Bits precision haben. Man kann also nicht beliebig
     * viele große Zahlen kombinieren, ohne in Uneindeutigkeiten zu gelangen.
     * Gilt normalerweise nur für natürliche Zahlen, wurde erweitert für ganze Zahlen! ;
     * Komplexitätsangaben für natürliche Zahlen.
     * Achtung, falls möglich, besser Maths.pairer verwenden, weil pairing schnell zu große Zahlen generiert!
     */
    static pairing(...k) {
        if (k.length === 0) {
            return 0;
        }
        let a = (k[0] < 0 ? -k[0] * 2 + 1 : k[0] * 2); //erweitert Standard Mapping von natürlichen auf ganze Zahlen
        for (let i = 1; i < k.length; i++) {
            const b = (k[i] < 0 ? -k[i] * 2 + 1 : k[i] * 2); //erweitert Standard Mapping von natürlichen auf ganze Zahlen
            a = 0.5 * (a + b) * (a + b + 1) + b;
        }
        if (Math.abs(a)>Number.MAX_SAFE_INTEGER) {
            console.warn('Maths.pairing('+k.join(', ')+')='+a,' may not be unique because it is a float number');
        }
        if (_.isNaN(a)) {
            throw "not a number: " + a;
        }
        return a;
    }

    /**
     * Erzeugt aus n Zahlen eine neue eindeutige Zahl, ähnlich wie Cantor-Pairing, aber mit definierbarem
     * Zahlenumfang der einzelnen Bereiche
     * @param sizes
     * @example const pairer=Maths.pairer(4,3,2);
     * pairer(0,0,0)=0
     * pairer(4,0,0)=4
     * pairer(-1,0,0)=5
     * pairer(0,1,0)=9
     * pairer(-4,-3,0)=62
     * pairer(0,0,1)=63
     */
    static pairer(...sizes) {
        let acc = 1;
        const pow = sizes.map(s=>{
            let r = acc;
            acc=(2*s+1)*acc;
            return r;
        });
        if (!Number.isSafeInteger(acc)) {
            throw "Math.pairer("+sizes.join(',')+") maximum "+acc+" is not a safe integer";
        }
        return (...k)=>{
            if (sizes.length!=k.length) {
                throw "pairing: sizes.length!=k.length";
            }
            let a = 0;
            for (let i=0;i<k.length;i++) {
                if (Math.abs(k[i])>sizes[i]) {
                    throw "pairing: k["+i+"]="+k[i]+" > sizes["+i+"]="+sizes[i];
                }
                a += (Math.abs(k[i])+(k[i]<0 ? sizes[i] : 0))*pow[i];
            }
            return a;
        };
    }

    /**
     * Formatiert eine positive Ganzzahl in einen String zu einer bestimmten Basis
     * @param number
     * @param baseString
     * @return {string}
     * @example
     * Maths.formatBase(0,'ABCDEFGHIJKLMNOPQRSTUVWXYZ') => "A"
     * Maths.formatBase(23232,'ABCDEFGHIJKLMNOPQRSTUVWXYZ') => "BIJO"
     */
    static formatBase(number, baseString) {
        const base = baseString.length;
        let result = '';
        number = Math.abs(Math.trunc(number));
        do {
            result = baseString[Math.trunc(number % base)] + result;
            number = Math.trunc(number / base);
        } while (number >= 1);
        return result;
    }

    /**
     * Umkehrfunktion zu formatBase
     * @param text
     * @param baseString
     * @returns {number}
     */
    static unformatBase(text, baseString) {
        const base = baseString.length;
        let result = 0;
        text = text.replace(new RegExp('[^' + baseString + ']', 'g'), '');
        for (let c of text) {
            result *= base;
            result += baseString.indexOf(c);
        }
        return result;
    }

    /**
     * Ganzzahlige Division
     * @param divident
     * @param divisor
     * @returns [ganzzahliger quotient,rest]
     * @example
     * div(5,2)=[2,1]
     * div(-5,2)=[-2,-1]
     * div(-5,-2)=[2,-1]
     * div(5,-2)=[-2,1]
     */
    static div(divident, divisor) {
        return [(divident - (divident % divisor)) / divisor, divident % divisor];
    }

    /**
     * Verteilt eine Ganzahl-Summe möglichst gleichmässig auf eine Anzahl von Array Elementen.
     * Sinnvoll z.B., um einen Geldbetrag möglichst gerecht aufzuteilen, ohne dass Centbeträge
     * durch Rundungsfehler verloren gehen. Die Aufteilung bevorzugt die ersten Elemente gegenüber
     * den letzten - die ersten n Elemente haben jeweils 1 Cent mehr als die letzten m Elemente.
     * Wenn man die Aufteilung wirklich fair machen möchte, dann müssen die Elemente anschließend
     * noch gemischt werden: _.shuffle(Maths.gleichverteilt(...))
     * @param betrag der zu verteilende Betrag
     * @param anzahlElemente die Anzahl der Elemente
     * @param decimals die zu berücksichtigenden Nachkommastellen, also bei Euro 2 für "auf den Cent"
     * @return Array von aufgeteilten Teilbeträgen
     * @example
     * Maths.dividedEqually(10,3,2)=[3.34, 3.33, 3.33]
     */
    static dividedEqually(betrag, anzahlElemente, decimals=0) {
        const f = Math.pow(10,decimals);
        const anzahl = Math.floor(anzahlElemente);
        let [wert,rest] = Maths.div(Math.floor(betrag*f), anzahl);
        const werte = _.range(anzahl).map(()=>wert);
        let i = 0;
        while (rest>0) {
            werte[i++]++;
            rest--;
        }
        return werte.map(x=>x/f);
    }

    /** */
    static nearestPowerOfTwo(value) {
        return Math.pow(2, Math.round(Math.log(value) / Math.LN2));
    }

    /**
     * wandelt NaN oder alles, was keine Zahl ist, zu 0. Unendlich gilt als Zahl.
     * @param number
     * @return {number}
     */
    static nanToZero(number) {
        if (typeof number != 'number' || Number.isNaN(number)) {
            return 0;
        }
        return number;
    }

    /**
     * Ein seedbarer, schneller PRNG mit guter Qualität, nicht kryptografisch.
     * see https://stackoverflow.com/questions/521295/seeding-the-random-number-generator-in-javascript/47593316#47593316
     * @param seed
     * @returns {function()}
     * @example
     * // derselbe Seed führt zur exakt selben Zufallsfolge
     * const seed = 37475;
     * const prng = Maths.createPseudoRandomGeneratorMulberry32(seed);
     * console.log('1. Zufallszahl',prng());
     * console.log('2. Zufallszahl',prng());
     */
    static createPseudoRandomGeneratorMulberry32(seed=1) {
        return ()=>{
            let t = seed += 0x6D2B79F5;
            t = Math.imul(t ^ t >>> 15, t | 1);
            t ^= t + Math.imul(t ^ t >>> 7, t | 61);
            return ((t ^ t >>> 14) >>> 0) / 4294967296;
        }
    }
}


window.Maths = Maths;
